import React, { useState, useEffect, useRef } from 'react';
import { CgSpinner } from "react-icons/cg";
import Dropzone from 'react-dropzone';
import { RiDeleteBin6Fill } from "react-icons/ri";
import './formComponents.css';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


function formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}





export function FileDropzone(props) {

    const labelText = props.labelText || 'File Upload';
    const emptyText = props.emptyText || 'No files attached yet.';
    const noteText = props.noteText || 'Files will not be uploaded until the request is submitted.';
    const isDisabled = Boolean(props.disabled);
    const previousFilesRef = useRef([]);
    const useFullWidth = Boolean(props.fullWidth);
    const enableClipboard = Boolean(props.enableClipboard);

    const getOfficeFileType = (fileName) => {
        if (!fileName) {
            return null;
        }
        const extension = fileName.split('.').pop().toLowerCase();
        if (['doc', 'docx', 'dot', 'dotx', 'rtf'].includes(extension)) {
            return 'word';
        }
        if (['xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'csv'].includes(extension)) {
            return 'excel';
        }
        if (['ppt', 'pptx', 'pps', 'ppsx', 'pot', 'potx'].includes(extension)) {
            return 'powerpoint';
        }
        return null;
    };

    useEffect(() => {
        const previousFiles = previousFilesRef.current;
        const currentFiles = props.filesState;

        const removedFiles = previousFiles.filter(
            (file) => !currentFiles.includes(file)
        );

        removedFiles.forEach((file) => {
            if (file.previewUrl) {
                URL.revokeObjectURL(file.previewUrl);
            }
        });

        previousFilesRef.current = currentFiles;
    }, [props.filesState]);

    function removeFile(targetIndex) {
        if (isDisabled) {
            return;
        }
        const updatedFiles = props.filesState.filter((_, index) => index !== targetIndex);
        props.fileStateSet(updatedFiles);
    }

    function handleFile(acceptedFiles) {
        if (isDisabled) {
            return;
        }
        const newFiles = acceptedFiles.map(file => ({
            fileName: file.name,
            file,
            fileSize: file.size,
            previewUrl: file.type && file.type.startsWith('image/')
                ? URL.createObjectURL(file)
                : null,
            officeType: getOfficeFileType(file.name)
        }));

        const combinedFiles = [...props.filesState, ...newFiles];

        if (props.maxFileSizeMB) {
            const oversizedFile = newFiles.find(
                (file) => file.fileSize > props.maxFileSizeMB * 1024 * 1024
            );
            if (oversizedFile) {
                toast.error(`The maximum size for each file is ${props.maxFileSizeMB}MB.`);
                return;
            }
        }

        // Limit to 10 files
        if (combinedFiles.length > props.maxFiles) {
            let toastString = 'You can only attach up to ' + props.maxFiles +  ' files.';
            toast.error(toastString);
            return;
        }

        // Limit to MB total
        const totalSize = combinedFiles.reduce((acc, f) => acc + f.fileSize, 0);
        if (totalSize > props.maxTotalSizeMB * 1024 * 1024) {
            toast.error('Total file size has been exceeded.');
            return;
        }

        props.fileStateSet(combinedFiles);
    }

    async function handleClipboardAttach() {
        if (isDisabled) {
            return;
        }
        if (!navigator.clipboard || !navigator.clipboard.read) {
            toast.error('Clipboard access is not supported in this browser.');
            return;
        }
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
                const imageType = item.types.find((type) => type.startsWith('image/'));
                if (!imageType) {
                    continue;
                }
                const blob = await item.getType(imageType);
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const extension = imageType.split('/').pop() || 'png';
                const file = new File([blob], `clipboard-${timestamp}.${extension}`, { type: imageType });
                handleFile([file]);
                return;
            }
            toast.error('Clipboard does not contain an image.');
        } catch (error) {
            toast.error('Could not read clipboard contents.');
        }
    }




    return (
        <div className='formRow'>
            <div className='requestLabelDiv'>
                <label className='requestLabel'>{labelText}</label>
            </div>
            <div className='requestInputDiv'>
                <Dropzone
                    onDrop={(acceptedFiles) => handleFile(acceptedFiles)}
                    onDropRejected={() => toast.error('One or more files are not an allowed file type.')}
                    disabled={isDisabled}
                    accept={props.accept}
                    multiple={props.multiple !== false}
                >

                    {({ getRootProps, getInputProps }) => (
                        <section>
                            <div className={`outerDropzoneDiv${useFullWidth ? ' outerDropzoneDivFull' : ''}`}>
                                <div className='dropzoneStyle' {...getRootProps()}>
                                    <label className='dropzoneTextStyle'>
                                        Drag 'n' drop some files here, or click to select files
                                    </label>
                                    <input {...getInputProps()} />
                                </div>
                            </div>
                        </section>
                    )}
                </Dropzone>
                {enableClipboard && (
                    <div className="dropzoneClipboardRow">
                        <button
                            type="button"
                            className="dropzoneClipboardButton"
                            onClick={handleClipboardAttach}
                            disabled={isDisabled}
                        >
                            Attach image from clipboard
                        </button>
                    </div>
                )}

            </div>

            <div className='filesDisplayDiv'>
                {props.filesState.length != 0 && (
                    <p className='dropzoneNote'>{noteText}</p>
                )}

                {props.filesState.length === 0 && (
                    <p style={{ fontStyle: 'italic', color: '#777' }}>{emptyText}</p>
                )}

                {props.filesState.map((file, i) => (
                    <div key={i} className={`attachedFileCard${useFullWidth ? ' attachedFileCardFull' : ''}`}>
                        <div className='attachedFileIcon' aria-hidden="true">
                            {file.previewUrl ? (
                                <img
                                    className='attachedFilePreview'
                                    src={file.previewUrl}
                                    alt=""
                                />
                            ) : file.officeType ? (
                                <div className={`attachedFileOfficeIcon attachedFileOfficeIcon--${file.officeType}`}>
                                    {file.officeType === 'word' && <span>W</span>}
                                    {file.officeType === 'excel' && <span>X</span>}
                                    {file.officeType === 'powerpoint' && <span>P</span>}
                                </div>
                            ) : (
                                <div className='attachedFileIconInner' />
                            )}
                        </div>
                        <div className='attachedFileDetails'>
                            <span className='attachedFileName'>{file.fileName}</span>
                            <span className='attachedFileMeta'>{formatBytes(file.fileSize)}</span>
                        </div>
                        <button
                            type='button'
                            onClick={() => removeFile(i)}
                            className='attachedFileRemoveButton'
                            aria-label={`Remove ${file.fileName}`}
                        >
                            <RiDeleteBin6Fill className='attachedFileRemoveIcon' />
                        </button>
                    </div>
                ))}

                {/* Show total size */}
                {props.filesState.length > 0 && (
                    <p style={{ fontStyle: 'italic', marginTop: '5px', color: '#444' }}>
                        Total size: {formatBytes(props.filesState.reduce((acc, f) => acc + f.fileSize, 0))} (Max {props.maxTotalSizeMB}MB)     {props.filesState.length} / {props.maxFiles}
                    </p>   
                )}

                


            </div>

        </div>
    );
}











export function SubmitButton(props) {
    return (
        <div className='submitButtonDiv'>
            <button className='formSubmitButton' type="submit" disabled={props.disabled || props.isLoading}>
                {props.isLoading ? (
                    <span className="submit-button-content">
                        <CgSpinner className="submit-button-spinner" />
                        <span className="submit-button-text">{props.loadingText || 'Submitting...'}</span>
                    </span>
                ) : (
                    props.text
                )}
            </button>
        </div>
    );
};


export function AsyncActionButton(props) {
    const {
        type = 'button',
        className = '',
        disabled = false,
        isLoading = false,
        loadingText = 'Loading...',
        children,
        ...restProps
    } = props;

    return (
        <button
            type={type}
            className={className}
            disabled={disabled || isLoading}
            {...restProps}
        >
            {isLoading ? (
                <span className="async-action-button-content">
                    <CgSpinner className="async-action-button-spinner" aria-hidden="true" />
                    <span className="async-action-button-text">{loadingText}</span>
                </span>
            ) : (
                children
            )}
        </button>
    );
}
